import { useEffect, useMemo, useRef, useState } from "react";
import API from "../../services/API";
import {
  hasKnownElementIdentity,
  isElementClone,
} from "../../utility/elementIdentity";

const unwrapElement = (response) => response?.data?.data || response?.data;

const useElementCloneStatuses = (elements = []) => {
  const [fetchedStatuses, setFetchedStatuses] = useState({});
  const requestedElementIdsRef = useRef(new Set());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    elements.forEach((element) => {
      const elementId = element?.id;
      if (
        !elementId ||
        hasKnownElementIdentity(element) ||
        requestedElementIdsRef.current.has(elementId)
      ) {
        return;
      }

      requestedElementIdsRef.current.add(elementId);

      API.get(`elements/${elementId}`, { requiresAuth: true })
        .then((response) => {
          const fullElement = unwrapElement(response);
          if (!mountedRef.current || !hasKnownElementIdentity(fullElement)) {
            return;
          }

          setFetchedStatuses((previousStatuses) => ({
            ...previousStatuses,
            [elementId]: isElementClone(fullElement),
          }));
        })
        .catch((error) => {
          requestedElementIdsRef.current.delete(elementId);
          console.error(`Error checking element identity for ${elementId}:`, error);
        });
    });
  }, [elements]);

  return useMemo(() => {
    const statuses = { ...fetchedStatuses };

    elements.forEach((element) => {
      if (element?.id && hasKnownElementIdentity(element)) {
        statuses[element.id] = isElementClone(element);
      }
    });

    return statuses;
  }, [elements, fetchedStatuses]);
};

export default useElementCloneStatuses;
